/**
 * features/hr/encashmentWrite.ts
 * ------------------------------------------------------------------
 * Recording, approving, rejecting and cancelling a sale of leave days.
 *
 * The decisions — is there balance, would this move unpaid leave already
 * charged — are made in lib/leaveEncashment and tested there. This file
 * only writes what those decisions allow, and the two names: who recorded
 * the sale and who approved it.
 *
 * NO AMOUNT IS EVER WRITTEN. What is paid, how it is worked out and who
 * authorises it belong to the finance office, outside this system.
 *
 * STATUS IS RE-READ INSIDE A TRANSACTION. Two people opening the same
 * pending sale can both press Approve; without the re-read, both writes
 * land and the record shows two approvals of one sale. The balance check is
 * made by the caller from live data a moment earlier — close enough for one
 * sale being approved at a desk.
 * ------------------------------------------------------------------ */
import { collection, doc, runTransaction } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { stripUndefined } from '@/lib/sanitize';
import { auditFromContext } from '@/lib/audit';
import {
  ENCASHMENT_COLLECTION, requestProblem, approvalProblem, cancelProblem,
  type HrLeaveEncashment,
} from '@/lib/leaveEncashment';

export interface Actor {
  uid?: string;
  email?: string;
  displayName?: string;
  role?: string;
  linkedStaffId?: string;
}

const nowIso = () => new Date().toISOString();

export interface RecordInput {
  staffId: string;
  staffName: string;
  staffStatus?: string;
  days: number;
  reason: string;
  /** From the same balance engine the leave screens use, with sales included. */
  available: number;
  actor: Actor;
}

export async function recordEncashment(input: RecordInput): Promise<string> {
  if (!input.actor.uid) throw new Error('Sign in again before recording a sale.');
  const bad = requestProblem({
    staffStatus: input.staffStatus, type: 'annual', days: input.days, available: input.available,
  });
  if (bad) throw new Error(bad);
  if (input.reason.trim().length < 3) throw new Error('Give a reason for the sale.');

  const ref = doc(collection(db, ENCASHMENT_COLLECTION));
  const at = nowIso();
  const record: Omit<HrLeaveEncashment, 'id'> = {
    staffId: input.staffId,
    staffName: input.staffName,
    type: 'annual',
    days: input.days,
    reason: input.reason.trim(),
    status: 'pending',
    requestedBy: input.actor.uid,
    requestedByName: input.actor.displayName || input.actor.email || '',
    requestedAt: at,
    createdAt: at,
    updatedAt: at,
  };

  await runTransaction(db, async (tx) => {
    tx.set(ref, stripUndefined(record));
  });

  auditFromContext(input.actor, 'leave_encashment_recorded', 'hr', {
    targetId: input.staffId, targetType: 'staff' as never, targetName: input.staffName,
    details: { encashmentId: ref.id, days: input.days, reason: record.reason },
  });
  return ref.id;
}

export async function approveEncashment(args: {
  encashment: HrLeaveEncashment;
  available: number;
  unpaidChanges: string[];
  note?: string;
  actor: Actor;
}): Promise<void> {
  const { encashment: e, actor } = args;
  const ref = doc(db, ENCASHMENT_COLLECTION, e.id);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That sale no longer exists.');
    const live = { id: snap.id, ...snap.data() } as HrLeaveEncashment;

    const bad = approvalProblem({
      encashment: live,
      available: args.available,
      unpaidChanges: args.unpaidChanges,
    });
    if (bad) throw new Error(bad);

    tx.update(ref, stripUndefined({
      status: 'approved',
      decidedBy: actor.uid,
      decidedByName: actor.displayName || actor.email || '',
      decidedAt: nowIso(),
      decisionNote: args.note?.trim() || undefined,
      updatedAt: nowIso(),
    }));
  });

  auditFromContext(actor, 'leave_encashment_approved', 'hr', {
    targetId: e.staffId, targetType: 'staff' as never, targetName: e.staffName,
    details: { encashmentId: e.id, days: e.days },
  });
}

export async function rejectEncashment(args: {
  encashment: HrLeaveEncashment;
  note: string;
  actor: Actor;
}): Promise<void> {
  const { encashment: e, actor } = args;
  if (args.note.trim().length < 3) throw new Error('Give a reason for rejecting it.');
  const ref = doc(db, ENCASHMENT_COLLECTION, e.id);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That sale no longer exists.');
    if (snap.data().status !== 'pending') throw new Error('Only a sale awaiting approval can be rejected.');
    tx.update(ref, {
      status: 'rejected',
      decidedBy: actor.uid ?? '',
      decidedByName: actor.displayName || actor.email || '',
      decidedAt: nowIso(),
      decisionNote: args.note.trim(),
      updatedAt: nowIso(),
    });
  });

  auditFromContext(actor, 'leave_encashment_rejected', 'hr', {
    targetId: e.staffId, targetType: 'staff' as never, targetName: e.staffName,
    details: { encashmentId: e.id, days: e.days, note: args.note.trim() },
  });
}

export async function cancelEncashment(args: {
  encashment: HrLeaveEncashment;
  reason: string;
  unpaidChanges: string[];
  actor: Actor;
}): Promise<void> {
  const { encashment: e, actor } = args;
  const ref = doc(db, ENCASHMENT_COLLECTION, e.id);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That sale no longer exists.');
    const live = { id: snap.id, ...snap.data() } as HrLeaveEncashment;
    const bad = cancelProblem({ encashment: live, reason: args.reason, unpaidChanges: args.unpaidChanges });
    if (bad) throw new Error(bad);
    tx.update(ref, {
      status: 'cancelled',
      cancelledBy: actor.uid ?? '',
      cancelledByName: actor.displayName || actor.email || '',
      cancelledAt: nowIso(),
      cancelReason: args.reason.trim(),
      updatedAt: nowIso(),
    });
  });

  auditFromContext(actor, 'leave_encashment_cancelled', 'hr', {
    targetId: e.staffId, targetType: 'staff' as never, targetName: e.staffName,
    details: { encashmentId: e.id, days: e.days, wasApproved: e.status === 'approved', reason: args.reason.trim() },
  });
}
